import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './MiniProductSummary.module.scss';
import { Image as FiImage } from '../../../components/Image';
import ValuePropsBox from '../../../views/ProductDetails/Series3CollarDetails/components/ValuePropsBox';
import { ReactComponent as IconDrip } from '../../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconFeather } from '../../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconBatteryCharging } from '../../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconCellular } from '../../../assets/images/icons/icon_cellular.svg';
import { ReactComponent as IconMagic } from '../../../assets/images/icons/icon_magic.svg';
import { isMobileDevice } from '../../../lib/util/isMobileDevice';
import VerizonLogo from '../../../assets/images/verizon-logo.png';
import { FullSpecsSection } from '../../../views/ProductDetails/Series3CollarDetails/components/FullSpecsSection/FullSpecsSection';
import { Species } from '../../../types/gql-op-types';

export default function MiniProductSummary({
  containerRef,
  species,
  hideHardwareImage = false,
}: {
  containerRef?: React.RefObject<HTMLDivElement>;
  species?: Species;
  /** Drops the unboxing shot, for surfaces that already lead with their own hero imagery. */
  hideHardwareImage?: boolean;
}) {
  const { t } = useTranslation();
  const specifications = isMobileDevice() ? t('specs') : t('specifications');
  const showFiIntelligence = species !== Species.CAT;

  return (
    <div ref={containerRef} className={styles.container}>
      {!hideHardwareImage && (
        <div className={styles.hardwareImage}>
          <FiImage
            image={{
              type: 'image',
              url: '/product_images/mini/unboxing.png',
            }}
          />
          <div className={styles.hardwareImageSubHeading}>{t('includes_fi_mini_device_accessories_and_1_cable')}</div>
        </div>
      )}
      <h2 className={styles.heading} style={{ textAlign: 'center' }}>
        {specifications}
      </h2>
      <div className={styles.valueProps}>
        <ValuePropsBox
          className={styles.valuePropsBox}
          columns={1}
          direction="column"
          valueProps={[
            {
              icon: <IconDrip />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('fully_waterproof')}</div>
                  <div className={styles.valuePropBody}>{t('ip68_tested_in_salt_water_no_dash')}</div>
                </div>
              ),
            },
            {
              icon: <IconFeather />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('small_and_versatile')}</div>
                  <div className={styles.valuePropBody}>{t('can_be_added_to_any_collar')}</div>
                </div>
              ),
            },
            {
              icon: <IconBatteryCharging />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('up_to_2_months_battery_life')}</div>
                  <div className={styles.valuePropBody}>{t('on_a_single_usb_c_charge')}</div>
                </div>
              ),
            },
            ...(showFiIntelligence
              ? [
                  {
                    icon: <IconMagic />,
                    text: (
                      <div className={styles.valueProp}>
                        <div className={styles.valuePropHeader}>{t('featuring_fi_intelligence')}</div>
                        <div className={styles.valuePropBody}>{t('ask_fi_anything_your_dog_decoded')}</div>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              icon: <IconCellular />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>
                    <img src={VerizonLogo} alt="Verizon" />
                  </div>
                  <div className={styles.valuePropBody}>{t('verizon_lte_m_cellular_network')}</div>
                </div>
              ),
            },
          ]}
        />
      </div>
      <FullSpecsSection isMini />
    </div>
  );
}
